import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Accordion
 *
 * Collapsible content sections for FAQs, settings, and grouped details.
 * Gold active accent, navy chevron, animated expand/collapse.
 *
 * Usage:
 *   <Accordion type="single" defaultValue="item-1">
 *     <AccordionItem value="item-1">
 *       <AccordionTrigger>What is JPG Academy?</AccordionTrigger>
 *       <AccordionContent>
 *         A Catholic, Classical School for Acadiana.
 *       </AccordionContent>
 *     </AccordionItem>
 *     <AccordionItem value="item-2">
 *       <AccordionTrigger>How do I order lunch?</AccordionTrigger>
 *       <AccordionContent>
 *         Use the Lunch Orders app from the sidebar.
 *       </AccordionContent>
 *     </AccordionItem>
 *   </Accordion>
 */

// ─── Context ────────────────────────────────────────────────────────────────

interface AccordionContextValue {
	openItems: Set<string>
	toggle: (value: string) => void
	type: 'single' | 'multiple'
}

const AccordionContext = React.createContext<AccordionContextValue>({
	openItems: new Set(),
	toggle: () => {},
	type: 'single',
})

interface AccordionItemContextValue {
	value: string
	isOpen: boolean
}

const AccordionItemContext = React.createContext<AccordionItemContextValue>({
	value: '',
	isOpen: false,
})

// ─── Accordion Root ─────────────────────────────────────────────────────────

export interface AccordionProps {
	/** Allow single or multiple open items */
	type?: 'single' | 'multiple'
	/** Default open item(s) */
	defaultValue?: string | string[]
	/** Controlled open item(s) */
	value?: string | string[]
	/** Change handler */
	onValueChange?: (value: string | string[]) => void
	children: React.ReactNode
	className?: string
}

export function Accordion({
	type = 'single',
	defaultValue,
	value: controlledValue,
	onValueChange,
	children,
	className,
}: AccordionProps) {
	const [internalValue, setInternalValue] = React.useState<Set<string>>(() => {
		if (defaultValue) {
			return new Set(Array.isArray(defaultValue) ? defaultValue : [defaultValue])
		}
		return new Set()
	})

	const isControlled = controlledValue !== undefined
	const openItems = isControlled
		? new Set(Array.isArray(controlledValue) ? controlledValue : [controlledValue])
		: internalValue

	const toggle = React.useCallback(
		(itemValue: string) => {
			const next = new Set(openItems)

			if (next.has(itemValue)) {
				next.delete(itemValue)
			} else {
				if (type === 'single') next.clear()
				next.add(itemValue)
			}

			if (!isControlled) setInternalValue(next)
			if (onValueChange) {
				onValueChange(type === 'single' ? (Array.from(next)[0] ?? '') : Array.from(next))
			}
		},
		[openItems, type, isControlled, onValueChange],
	)

	return (
		<AccordionContext.Provider value={{ openItems, toggle, type }}>
			<div className={cn('divide-y divide-border', className)}>
				{children}
			</div>
		</AccordionContext.Provider>
	)
}

// ─── Accordion Item ─────────────────────────────────────────────────────────

export interface AccordionItemProps {
	/** Unique value for this item */
	value: string
	children: React.ReactNode
	className?: string
}

export function AccordionItem({ value, children, className }: AccordionItemProps) {
	const { openItems } = React.useContext(AccordionContext)
	const isOpen = openItems.has(value)

	return (
		<AccordionItemContext.Provider value={{ value, isOpen }}>
			<div className={cn('py-1', className)} data-state={isOpen ? 'open' : 'closed'}>
				{children}
			</div>
		</AccordionItemContext.Provider>
	)
}

// ─── Accordion Trigger ──────────────────────────────────────────────────────

export interface AccordionTriggerProps {
	children: React.ReactNode
	className?: string
}

export function AccordionTrigger({ children, className, ...props }: AccordionTriggerProps) {
	const { toggle } = React.useContext(AccordionContext)
	const { value, isOpen } = React.useContext(AccordionItemContext)

	return (
		<button
			type="button"
			onClick={() => toggle(value)}
			aria-expanded={isOpen}
			className={cn(
				'flex items-center justify-between w-full py-3 px-1',
				'text-left font-ui font-medium text-sm text-foreground',
				'hover:text-navy-700 transition-colors duration-fast',
				'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30 focus-visible:ring-offset-1 rounded-sm',
				isOpen && 'text-navy-900',
				className,
			)}
			{...props}
		>
			<span className="flex-1 min-w-0">{children}</span>
			<motion.span
				className="flex-shrink-0 text-muted-foreground ml-2"
				animate={{ rotate: isOpen ? 180 : 0 }}
				transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
			>
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<path d="m6 9 6 6 6-6" />
				</svg>
			</motion.span>
		</button>
	)
}

// ─── Accordion Content ──────────────────────────────────────────────────────

export interface AccordionContentProps {
	children: React.ReactNode
	className?: string
}

export function AccordionContent({ children, className }: AccordionContentProps) {
	const { isOpen } = React.useContext(AccordionItemContext)

	return (
		<AnimatePresence initial={false}>
			{isOpen && (
				<motion.div
					initial={{ height: 0, opacity: 0 }}
					animate={{ height: 'auto', opacity: 1 }}
					exit={{ height: 0, opacity: 0 }}
					transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
					className="overflow-hidden"
				>
					<div className={cn('pb-3 px-1 text-sm font-body text-muted-foreground leading-relaxed', className)}>
						{children}
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
